import { domMax, LazyMotion, m } from "motion/react";
import * as React from "react";
import { cn } from "../lib/utils";
import { SPRING_TRANSITION_FAST } from "./shared/constants";

// @internal — font must be loaded via '@bug-on/m3-expressive/material-symbols.css'
const VARIANT_FONT: Record<NonNullable<IconProps["variant"]>, string> = {
	outlined: "'Material Symbols Outlined'",
	rounded: "'Material Symbols Rounded'",
	sharp: "'Material Symbols Sharp'",
};

/**
 * Props cho component {@link Icon}.
 *
 * All variable font axes are mapped directly to `font-variation-settings`.
 */
export interface IconProps extends React.HTMLAttributes<HTMLSpanElement> {
	/**
	 * Name of the Material Symbol in snake_case format.
	 * @example "home", "arrow_forward", "settings"
	 * @see https://fonts.google.com/icons
	 */
	name: string;

	/**
	 * Geometric style variant — maps to font family.
	 * @default "outlined"
	 */
	variant?: "outlined" | "rounded" | "sharp";

	/**
	 * `FILL` axis. `0` = outlined, `1` = filled.
	 * Features spring animation when `animateFill` is true.
	 * @default 0
	 */
	fill?: 0 | 1;

	/**
	 * `wght` axis — stroke weight. Should match surrounding text weight.
	 * @default 400
	 */
	weight?: 100 | 200 | 300 | 400 | 500 | 600 | 700;

	/**
	 * `GRAD` axis — fine-tunes visual weight without affecting layout dimensions.
	 * Use `-25` on dark backgrounds to offset light halation effects.
	 * @default 0
	 */
	grade?: -50 | -25 | 0 | 100 | 200;

	/**
	 * `opsz` axis — optical size in dp. Used to set `font-size` if `size` is not specified.
	 * Match rendered pixel size for best quality.
	 * @default 24
	 */
	opticalSize?: 20 | 24 | 40 | 48;

	/**
	 * Direct `font-size` override in px. `opsz` axis still respects `opticalSize`.
	 * @example size={18} opticalSize={20}
	 */
	size?: number | "inherit";

	/**
	 * Enables smooth spring animation when toggling FILL axis (uses `SPRING_TRANSITION_FAST`).
	 * Requires `motion` dependency.
	 * @default false
	 * @example <Icon name="favorite" fill={isLiked ? 1 : 0} animateFill />
	 */
	animateFill?: boolean;
}

const IconComponent = React.forwardRef<HTMLSpanElement, IconProps>(
	(
		{
			name,
			variant = "outlined",
			fill = 0,
			weight = 400,
			grade = 0,
			opticalSize = 24,
			size,
			animateFill = false,
			className,
			style,
			...restProps
		},
		ref,
	) => {
		const fontVariationSettings = `'FILL' ${fill}, 'wght' ${weight}, 'GRAD' ${grade}, 'opsz' ${opticalSize}`;

		const computedStyle: React.CSSProperties = {
			fontFamily: VARIANT_FONT[variant],
			fontSize:
				size === "inherit"
					? "inherit"
					: size != null
						? `${size}px`
						: `${opticalSize}px`,
			fontVariationSettings,
			...style,
		};

		if (animateFill) {
			return (
				<LazyMotion features={domMax} strict>
					<m.span
						ref={ref}
						className={cn(
							"md-icon inline-flex items-center justify-center shrink-0 select-none",
							className,
						)}
						aria-hidden="true"
						animate={{ fontVariationSettings }}
						transition={SPRING_TRANSITION_FAST}
						style={computedStyle}
						// biome-ignore lint/suspicious/noExplicitAny: motion v12 HTMLMotionProps conflicts with React's event types
						{...(restProps as any)}
					>
						{name}
					</m.span>
				</LazyMotion>
			);
		}

		return (
			<span
				ref={ref}
				className={cn(
					"md-icon inline-flex items-center justify-center shrink-0 select-none",
					className,
				)}
				aria-hidden="true"
				style={computedStyle}
				{...restProps}
			>
				{name}
			</span>
		);
	},
);

IconComponent.displayName = "Icon";

/**
 * Material Symbols (variable font) Icon component.
 *
 * Make sure to import the icon font CSS before usage:
 * ```ts
 * import '@bug-on/m3-expressive/material-symbols-cdn.css';
 * ```
 *
 * @remarks
 * - Use snake_case for icon names: `"arrow_forward"`, NOT `"ArrowForward"`.
 * - `aria-hidden="true"` is attached automatically — provide screen reader labels on parent elements.
 *
 * @example
 * ```tsx
 * // Basic Icon
 * <Icon name="home" />
 *
 * // Visual customization (filled, heavy weight)
 * <Icon name="favorite" variant="rounded" fill={1} weight={300} />
 *
 * // Animated transition on state change
 * <Icon name="bookmark" fill={saved ? 1 : 0} animateFill />
 *
 * // Custom size override
 * <Icon name="close" size={18} opticalSize={20} />
 *
 * // Combination with other components
 * <Button icon={<Icon name="add" />}>Add to cart</Button>
 * ```
 *
 * @see https://fonts.google.com/icons
 * @see https://m3.material.io/styles/icons/overview
 */
export const Icon = React.memo(IconComponent);
